import React, { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  Table as MuiTable,
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material'

import { Button, Typography } from '~components/internal'

import './Table.scss'

export type TableSortDirection = 'asc' | 'desc'
export type TableAlign = 'left' | 'right' | 'center'

export interface TableHeader<T> {
  id: string
  label?: React.ReactNode
  render: (row: T, rowIndex: number) => React.ReactNode
  sortable?: boolean
  align?: TableAlign
  width?: number
  minWidth?: number
  ariaLabel?: string
  /**
   * Bound the body cell to the column's declared `width`/`maxWidth` instead of
   * letting its content grow the column. Use for columns whose content can wrap
   * (e.g. accumulating chips) so the column doesn't run over.
   */
  bounded?: boolean
}

export interface TableProps<T extends { id: string | number }> {
  headers: TableHeader<T>[]
  data: T[]
  sortBy?: string
  sortDirection?: TableSortDirection
  onSortChange?: (sortBy: string, sortDirection: TableSortDirection) => void
  selectable?: boolean
  isRowSelectable?: (row: T) => boolean
  reserveSelectionSpace?: boolean
  selectedIds?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
  selectionActions?: Array<{
    label: string
    onClick: (selectedIds: string[]) => void
    variant?: 'text' | 'contained' | 'outlined'
    disabled?: boolean
  }>
  /** Optional banner rendered inside the selection toolbar (e.g. "Select all N results" prompt). */
  selectAllMatchingBanner?: React.ReactNode
  /** Overrides the displayed selection count (e.g. when "select all matching" is active). */
  selectionCountOverride?: number
  resizableColumns?: boolean
  fixedLayout?: boolean
  sortDisabled?: boolean
  rightSort?: boolean
  size?: 'small' | 'medium'
  onRowClick?: (row: T) => void
  getRowClassName?: (row: T) => string | undefined
  /** Optional full-width content rendered in its own row directly below a row. */
  renderRowDetail?: (row: T) => React.ReactNode
  emptyMessage?: React.ReactNode
  caption?: React.ReactNode
  captionVisible?: boolean
  ariaLabel?: string
  divider?: boolean
  toolbar?: React.ReactNode
  contentHeader?: React.ReactNode
  pagination?: React.ReactNode
  loading?: boolean
  skeletonRowCount?: number
}

const OverflowTooltip: React.FC<{ label: string }> = ({ label }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [overflowing, setOverflowing] = useState(false)

  const checkOverflow = useCallback(() => {
    const el = ref.current
    if (el) setOverflowing(el.scrollWidth > el.clientWidth)
  }, [])

  return (
    <Tooltip title={overflowing ? label : ''} enterDelay={500}>
      <span ref={ref} className='internal-table__head-label-text' onMouseEnter={checkOverflow}>
        {label}
      </span>
    </Tooltip>
  )
}

const RESIZE_STEP = 8
const DEFAULT_MIN_WIDTH = 60
const SKELETON_ROW_MULTIPLIERS = [0.55, 0.7, 0.8, 0.6, 0.75]

function getSkeletonWidth(rowIndex: number): string {
  return `${Math.round(SKELETON_ROW_MULTIPLIERS[rowIndex % SKELETON_ROW_MULTIPLIERS.length] * 100)}%`
}

interface ResizeState {
  headerId: string
  startX: number
  startWidth: number
}

function Table<T extends { id: string | number }>({
  headers,
  data,
  sortBy,
  sortDirection = 'asc',
  onSortChange,
  selectable = false,
  isRowSelectable = (_row: T): boolean => true,
  reserveSelectionSpace = false,
  selectedIds,
  onSelectionChange,
  selectionActions,
  selectAllMatchingBanner,
  selectionCountOverride,
  resizableColumns = false,
  fixedLayout = false,
  sortDisabled = false,
  rightSort = true,
  size = 'medium',
  onRowClick,
  getRowClassName,
  renderRowDetail,
  emptyMessage = 'No results',
  caption,
  captionVisible = false,
  ariaLabel,
  divider = true,
  toolbar,
  contentHeader,
  pagination,
  loading = false,
  skeletonRowCount = 10,
}: TableProps<T>): ReactElement {
  // --------------------------------------------------------------------
  // Column widths (only used when `resizableColumns` is enabled)
  // --------------------------------------------------------------------
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {}
    for (const h of headers) {
      if (h.width != null) initial[h.id] = h.width
    }
    return initial
  })

  const resizeStateRef = useRef<ResizeState | null>(null)
  const [activeResizeId, setActiveResizeId] = useState<string | null>(null)

  const getHeaderWidth = (h: TableHeader<T>): number | undefined => columnWidths[h.id] ?? h.width

  const clampWidth = useCallback((h: TableHeader<T>, next: number): number => {
    const min = h.minWidth ?? DEFAULT_MIN_WIDTH
    return Math.max(min, Math.round(next))
  }, [])

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, h: TableHeader<T>) => {
      if (!resizableColumns) return
      event.preventDefault()
      event.stopPropagation()

      const headerCell = (event.currentTarget.parentElement as HTMLElement | null) ?? null
      const startWidth = getHeaderWidth(h) ?? headerCell?.getBoundingClientRect().width ?? 0
      resizeStateRef.current = {
        headerId: h.id,
        startX: event.clientX,
        startWidth,
      }
      setActiveResizeId(h.id)
    },
    [columnWidths, resizableColumns],
  )

  useEffect(() => {
    if (!activeResizeId) return

    const handlePointerMove = (event: PointerEvent): void => {
      const state = resizeStateRef.current
      if (!state) return
      const h = headers.find((x) => x.id === state.headerId)
      if (!h) return
      const nextWidth = clampWidth(h, state.startWidth + (event.clientX - state.startX))
      setColumnWidths((prev) => ({ ...prev, [h.id]: nextWidth }))
    }

    const handlePointerUp = (): void => {
      resizeStateRef.current = null
      setActiveResizeId(null)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return (): void => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [activeResizeId, clampWidth, headers])

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, h: TableHeader<T>) => {
      if (!resizableColumns) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      const delta = event.key === 'ArrowLeft' ? -RESIZE_STEP : RESIZE_STEP
      const fallbackWidth =
        (event.currentTarget.parentElement as HTMLElement | null)?.getBoundingClientRect().width ??
        0
      setColumnWidths((prev) => {
        const current = prev[h.id] ?? h.width ?? fallbackWidth
        return { ...prev, [h.id]: clampWidth(h, current + delta) }
      })
    },
    [clampWidth, resizableColumns],
  )

  // --------------------------------------------------------------------
  // Selection
  // --------------------------------------------------------------------
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds])

  const visibleIds = data.filter(isRowSelectable).map((row) => String(row.id))

  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id))
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selectedSet.has(id))

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return
    if (allVisibleSelected || someVisibleSelected) {
      const next = (selectedIds ?? []).filter((id) => !visibleIds.includes(id))
      onSelectionChange(next)
    } else {
      const next = new Set(selectedIds ?? [])
      for (const id of visibleIds) next.add(id)
      onSelectionChange([...next])
    }
  }, [allVisibleSelected, someVisibleSelected, onSelectionChange, selectedIds, visibleIds])

  const handleToggleRow = useCallback(
    (id: string) => {
      if (!onSelectionChange) return
      const next = new Set(selectedIds ?? [])
      if (next.has(id)) next.delete(id)
      else next.add(id)
      onSelectionChange([...next])
    },
    [onSelectionChange, selectedIds],
  )

  const clearSelection = useCallback(() => {
    onSelectionChange?.([])
  }, [onSelectionChange])

  // --------------------------------------------------------------------
  // Sorting
  // --------------------------------------------------------------------
  const handleSortClick = useCallback(
    (h: TableHeader<T>) => {
      if (!h.sortable || sortDisabled || !onSortChange) return
      const nextDirection: TableSortDirection =
        sortBy === h.id && sortDirection === 'asc' ? 'desc' : 'asc'
      onSortChange(h.id, nextDirection)
    },
    [onSortChange, sortBy, sortDirection, sortDisabled],
  )

  // --------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------
  // `align` on the TableCell prop handles text-align; modifier classes are
  // only needed when we attach feature-specific styling to a cell.
  const headCellClassName = (h: TableHeader<T>, index: number): string => {
    const classes = ['internal-table__head-cell']
    if (h.sortable) classes.push('internal-table__head-cell--sortable')
    if (selectable && index === 0) classes.push('internal-table__head-cell--with-checkbox')
    if (size === 'small') classes.push('internal-table__head-cell--small')
    return classes.join(' ')
  }

  const bodyCellClassName = (h: TableHeader<T>, index: number): string => {
    const classes = ['internal-table__body-cell-root']
    if (selectable && index === 0) classes.push('internal-table__body-cell--with-checkbox')
    if (size === 'small') classes.push('internal-table__body-cell--small')
    if (h.sortable && h.align === 'right' && rightSort !== false) {
      classes.push('internal-table__body-cell--sort-offset')
    }
    return classes.join(' ')
  }

  const columnCount = headers.length

  const selectionCount = selectedSet.size
  const showSelectionToolbar =
    selectable && selectionCount > 0 && (selectionActions?.length ?? 0) > 0

  const tableClassName = [
    'internal-table',
    resizableColumns ? 'internal-table--resizable' : '',
    fixedLayout ? 'internal-table--fixed' : '',
    !divider ? 'internal-table--no-divider' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box className='internal-table-root'>
      {toolbar}
      {showSelectionToolbar && (
        <Box className='internal-table__selection-toolbar' role='region' aria-live='polite'>
          <Typography variant='body2'>
            <strong>{selectionCountOverride ?? selectionCount}</strong> selected
          </Typography>
          <Box className='internal-table__selection-toolbar-actions'>
            <Button size='small' variant='text' onClick={clearSelection}>
              Clear
            </Button>
            {selectAllMatchingBanner}
            {selectionActions?.map((action, index) => (
              <Button
                key={index}
                size='small'
                variant={action.variant || 'contained'}
                disabled={action.disabled || false}
                onClick={() => action.onClick([...selectedSet])}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Box>
      )}
      {contentHeader}
      <div className='internal-table-container'>
        <TableContainer>
          <MuiTable className={tableClassName} aria-label={ariaLabel}>
            {caption && (
              <caption
                className={captionVisible ? undefined : 'internal-table-caption--visually-hidden'}
              >
                {caption}
              </caption>
            )}
            <TableHead>
              <TableRow className='internal-table__head-row'>
                {headers.map((h, headerIndex) => {
                  const isSorted = sortBy === h.id
                  const width = getHeaderWidth(h)
                  return (
                    <TableCell
                      key={h.id}
                      className={headCellClassName(h, headerIndex)}
                      scope='col'
                      align={h.align}
                      aria-sort={
                        h.sortable
                          ? isSorted
                            ? sortDirection === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                      style={{
                        width,
                        minWidth: h.minWidth ?? (resizableColumns ? undefined : h.width),
                        maxWidth: resizableColumns ? undefined : h.width,
                      }}
                    >
                      <Box
                        className={[
                          'internal-table__head-cell-content',
                          size === 'small' && 'internal-table__head-cell-content--small',
                          h.align === 'right' && 'internal-table__head-cell-content--right',
                          h.align === 'center' && 'internal-table__head-cell-content--center',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {selectable && headerIndex === 0 && (
                          <Checkbox
                            size={size === 'small' ? 'small' : undefined}
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected}
                            onChange={handleToggleAll}
                            slotProps={{
                              input: {
                                'aria-label': !(allVisibleSelected || someVisibleSelected)
                                  ? 'Select all rows'
                                  : allVisibleSelected
                                    ? 'Deselect all rows'
                                    : `Deselect ${visibleIds.filter((id) => selectedSet.has(id)).length} rows`,
                              },
                            }}
                          />
                        )}
                        {h.sortable ? (
                          <TableSortLabel
                            active={!sortDisabled && isSorted}
                            disabled={sortDisabled}
                            direction={isSorted ? sortDirection : 'asc'}
                            onClick={(): void => handleSortClick(h)}
                            className={
                              rightSort === false ? 'internal-table__sort-label--left' : undefined
                            }
                            aria-label={
                              typeof h.label === 'string' ? `Sort by ${h.label}` : h.ariaLabel
                            }
                          >
                            {typeof h.label === 'string' ? (
                              <OverflowTooltip label={h.label} />
                            ) : (
                              h.label
                            )}
                          </TableSortLabel>
                        ) : typeof h.label === 'string' ? (
                          <OverflowTooltip label={h.label} />
                        ) : (
                          h.label
                        )}
                      </Box>
                      {resizableColumns && (
                        <button
                          type='button'
                          className={[
                            'internal-table__resize-handle',
                            activeResizeId === h.id ? 'internal-table__resize-handle--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          role='separator'
                          aria-orientation='vertical'
                          aria-label={
                            typeof h.label === 'string'
                              ? `Resize ${h.label} column`
                              : (h.ariaLabel ?? 'Resize column')
                          }
                          aria-valuenow={width}
                          aria-valuemin={h.minWidth ?? DEFAULT_MIN_WIDTH}
                          onPointerDown={(e): void => handleResizePointerDown(e, h)}
                          onKeyDown={(e): void => handleResizeKeyDown(e, h)}
                          onClick={(e): void => e.stopPropagation()}
                        />
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                  <TableRow key={rowIndex} className='internal-table__body-row'>
                    {headers.map((h, headerIndex) => (
                      <TableCell
                        key={h.id}
                        className={bodyCellClassName(h, headerIndex)}
                        align={h.align}
                      >
                        <Box
                          className={[
                            'internal-table__body-cell',
                            size === 'small' ? 'internal-table__body-cell--small' : '',
                            h.align === 'right' ? 'internal-table__body-cell--right' : '',
                            h.align === 'center' ? 'internal-table__body-cell--center' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {selectable && headerIndex === 0 && (
                            <Skeleton
                              variant='rounded'
                              className='internal-table__skeleton-checkbox'
                            />
                          )}
                          <Skeleton variant='text' width={getSkeletonWidth(rowIndex)} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell className='internal-table__empty-cell' colSpan={columnCount}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => {
                  const id = String(row.id)
                  const rowSelectable = selectable && isRowSelectable(row)
                  const isSelected = selectedSet.has(id)
                  const interactive = !!onRowClick
                  const rowClass = [
                    'internal-table__body-row',
                    interactive ? 'internal-table__body-row--interactive' : '',
                    getRowClassName?.(row),
                  ]
                    .filter(Boolean)
                    .join(' ')
                  const rowDetail = renderRowDetail?.(row)
                  return (
                    <React.Fragment key={id}>
                      <TableRow
                        hover
                        className={rowClass}
                        selected={isSelected}
                        aria-selected={rowSelectable ? isSelected : undefined}
                        onClick={interactive ? (): void => onRowClick?.(row) : undefined}
                        onKeyDown={
                          interactive
                            ? (e): void => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  onRowClick?.(row)
                                }
                              }
                            : undefined
                        }
                        tabIndex={interactive ? 0 : undefined}
                      >
                        {headers.map((h, headerIndex) => (
                          <TableCell
                            key={h.id}
                            className={bodyCellClassName(h, headerIndex)}
                            align={h.align}
                            style={
                              h.bounded
                                ? {
                                    width: getHeaderWidth(h),
                                    minWidth:
                                      h.minWidth ?? (resizableColumns ? undefined : h.width),
                                    maxWidth: resizableColumns ? undefined : h.width,
                                  }
                                : undefined
                            }
                          >
                            {((): ReactElement => {
                              const rendered = h.render(row, rowIndex)
                              const isNumeric = !h.align && typeof rendered === 'number'
                              return (
                                <Box
                                  className={[
                                    'internal-table__body-cell',
                                    size === 'small' ? 'internal-table__body-cell--small' : '',
                                    h.align === 'right' || isNumeric
                                      ? 'internal-table__body-cell--right'
                                      : '',
                                    h.align === 'center' ? 'internal-table__body-cell--center' : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  {headerIndex === 0 &&
                                    (rowSelectable ? (
                                      <Checkbox
                                        size={size}
                                        checked={isSelected}
                                        onChange={(): void => handleToggleRow(id)}
                                        onClick={(e): void => e.stopPropagation()}
                                        slotProps={{
                                          input: { 'aria-label': `Select row ${id}` },
                                        }}
                                      />
                                    ) : selectable && reserveSelectionSpace ? (
                                      <Checkbox
                                        className='internal-table__selection-placeholder'
                                        size={size === 'small' ? 'small' : undefined}
                                        disabled
                                        aria-hidden='true'
                                        tabIndex={-1}
                                      />
                                    ) : null)}
                                  {rendered}
                                </Box>
                              )
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                      {rowDetail && (
                        <TableRow className='internal-table__detail-row'>
                          <TableCell
                            colSpan={headers.length}
                            className='internal-table__detail-cell'
                          >
                            {rowDetail}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </div>
      {pagination}
    </Box>
  )
}

export default Table
